import { cn } from '@/lib/utils/cn'
import { FilterRail } from '@/components/ui/FilterRail'
import {
  DATE_PRESETS,
  type DateRange,
  type PresetValue,
} from './date-presets'

/**
 * Filter rail for /margins (Sprint 09 Item 1).
 *
 * Composes the shared `<FilterRail>` shell (see `@/components/ui/FilterRail`)
 * for the collapse/expand chrome — this file owns only the rail's own
 * content (date presets). Extracted so a second consumer (Product↔Customer
 * Pricing's `PricingFilterRail`) could compose the same shell instead of
 * duplicating it.
 *
 * Active preset is signaled by `aria-pressed="true"` on the chip
 * button (matches the existing margin group-by chips for screen-
 * reader consistency). Clicking the active chip clears the selection.
 */

export interface MarginsFilterRailProps {
  /** Currently active preset, or null when "Custom range" applies. */
  preset: PresetValue | null
  onPresetChange: (next: PresetValue | null) => void
  /** Resolved date range — bound to the always-editable From/To inputs
   *  below the preset chips so users see (and can override) exactly
   *  what date_from / date_to the active preset evaluates to. */
  dates: DateRange
  /** Fired when either date input is edited directly — switches the
   *  page into custom-range mode (preset becomes null). */
  onCustomRangeChange: (next: DateRange) => void
  collapsed: boolean
  onToggleCollapsed: () => void
}

export function MarginsFilterRail({
  preset,
  onPresetChange,
  dates,
  onCustomRangeChange,
  collapsed,
  onToggleCollapsed,
}: MarginsFilterRailProps) {
  return (
    <FilterRail
      collapsed={collapsed}
      onToggleCollapsed={onToggleCollapsed}
      activeCount={preset ? 1 : 0}
    >
      <section className="flex flex-col gap-1.5">
        <span className="text-role-section-label">Period</span>
        <div className="flex flex-wrap gap-1.5">
          {DATE_PRESETS.map((p) => {
            const active = preset === p.value
            return (
              <button
                key={p.value}
                type="button"
                aria-pressed={active}
                onClick={() => onPresetChange(active ? null : p.value)}
                className={cn(
                  'inline-flex items-center rounded-xs border border-border-default bg-surface px-2 py-0.5 text-xs text-primary hover:bg-surface-hover',
                  'aria-pressed:border-accent aria-pressed:bg-accent-subtle aria-pressed:text-accent-text',
                )}
              >
                {p.label}
              </button>
            )
          })}
        </div>
        <div className="mt-1 flex items-center gap-1.5 text-xs text-secondary">
          <input
            type="date"
            value={dates.from}
            aria-label="From"
            onChange={(e) => onCustomRangeChange({ ...dates, from: e.target.value })}
            className="h-6 w-[var(--date-input-width)] rounded-xs border border-border-default bg-surface px-1.5 text-xs text-primary tabular-nums"
          />
          <span aria-hidden="true">→</span>
          <input
            type="date"
            value={dates.to}
            aria-label="To"
            onChange={(e) => onCustomRangeChange({ ...dates, to: e.target.value })}
            className="h-6 w-[var(--date-input-width)] rounded-xs border border-border-default bg-surface px-1.5 text-xs text-primary tabular-nums"
          />
        </div>
      </section>
    </FilterRail>
  )
}

// Re-exports moved to date-presets.ts directly so this file only
// exports a component (Fast Refresh contract).
